import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface ForecastRow {
  month: string;
  expected_inflow: number;
  expected_outflow: number;
  net_cashflow: number;
  closing_balance: number;
  client_count: number;
}

interface AccountBalance {
  account: string;
  balance: number;
}

interface ForecastData {
  opening_balance: number;
  account_balances: AccountBalance[];
  forecast: ForecastRow[];
}

interface VarianceData {
  month: string;
  forecast_inflow: number;
  actual_inflow: number;
  inflow_variance: number;
  forecast_outflow: number;
  actual_outflow: number;
  outflow_variance: number;
  net_variance: number;
}

const CashflowForecast: React.FC = () => {
  const router = useRouter();
  const [tabValue, setTabValue] = useState(0);
  const [forecastData, setForecastData] = useState<ForecastData | null>(null);
  const [varianceData, setVarianceData] = useState<VarianceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [monthsAhead, setMonthsAhead] = useState(12);
  const [varianceMonths, setVarianceMonths] = useState(6);

  useEffect(() => {
    if (tabValue === 0) {
      fetchForecast();
    } else {
      fetchVarianceHistory();
    }
  }, [tabValue, monthsAhead, varianceMonths]);

  const fetchForecast = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/cashflow/forecast?monthsAhead=${monthsAhead}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setForecastData(data);
      }
    } catch (error) {
      console.error("Error fetching forecast:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVarianceHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/cashflow/variance-history?months=${varianceMonths}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setVarianceData(data);
      }
    } catch (error) {
      console.error("Error fetching variance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportForecast = () => {
    if (!forecastData) return;

    const headers = [
      "Month",
      "Expected Inflow",
      "Expected Outflow",
      "Net Cashflow",
      "Closing Balance",
      "Client Count",
    ];

    const csvRows = forecastData.forecast.map((row) =>
      [
        row.month,
        row.expected_inflow.toFixed(2),
        row.expected_outflow.toFixed(2),
        row.net_cashflow.toFixed(2),
        row.closing_balance.toFixed(2),
        row.client_count,
      ].join(","),
    );

    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashflow-forecast-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
  };

  const handleExportVariance = () => {
    if (!varianceData.length) return;

    const headers = [
      "Month",
      "Forecast Inflow",
      "Actual Inflow",
      "Inflow Variance",
      "Forecast Outflow",
      "Actual Outflow",
      "Outflow Variance",
      "Net Variance",
    ];

    const csvRows = varianceData.map((row) =>
      [
        row.month,
        row.forecast_inflow.toFixed(2),
        row.actual_inflow.toFixed(2),
        row.inflow_variance.toFixed(2),
        row.forecast_outflow.toFixed(2),
        row.actual_outflow.toFixed(2),
        row.outflow_variance.toFixed(2),
        row.net_variance.toFixed(2),
      ].join(","),
    );

    const csv = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashflow-variance-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
  };

  const getMonthName = (monthKey: string) => {
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const totalInflow = forecastData?.forecast.reduce(
    (s, r) => s + r.expected_inflow,
    0,
  );
  const totalOutflow = forecastData?.forecast.reduce(
    (s, r) => s + r.expected_outflow,
    0,
  );
  const netCashflow = (totalInflow || 0) - (totalOutflow || 0);

  return (
    <FinanceContainer>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => router.push("/finance")}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Cashflow Forecast
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={tabValue === 0 ? fetchForecast : fetchVarianceHistory}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={
                tabValue === 0 ? handleExportForecast : handleExportVariance
              }
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_e, v) => setTabValue(v)}
            indicatorColor="primary"
          >
            <Tab
              label="Forecast"
              icon={<TrendingUpIcon />}
              iconPosition="start"
            />
            <Tab
              label="Variance Analysis"
              icon={<AssessmentIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : tabValue === 0 ? (
          <>
            {/* Forecast Controls */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Months Ahead</InputLabel>
                <Select
                  value={monthsAhead}
                  label="Months Ahead"
                  onChange={(e) => setMonthsAhead(Number(e.target.value))}
                >
                  <MenuItem value={3}>3 Months</MenuItem>
                  <MenuItem value={6}>6 Months</MenuItem>
                  <MenuItem value={12}>12 Months</MenuItem>
                  <MenuItem value={24}>24 Months</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Summary Cards */}
            {forecastData && (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AccountBalanceIcon color="primary" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Opening Balance
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                        {formatCurrency(forecastData.opening_balance)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {forecastData.account_balances.length} accounts
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TrendingUpIcon color="success" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Expected Inflows
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ mt: 1, color: "#2e7d32" }}
                      >
                        {formatCurrency(totalInflow || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Over {monthsAhead} months
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <TrendingDownIcon color="error" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Expected Outflows
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ mt: 1, color: "#d32f2f" }}
                      >
                        {formatCurrency(totalOutflow || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Creditor payments due
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AccountBalanceIcon
                          color={netCashflow >= 0 ? "success" : "error"}
                        />
                        <Typography variant="subtitle2" color="text.secondary">
                          Net Cashflow
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          mt: 1,
                          color: netCashflow >= 0 ? "#2e7d32" : "#d32f2f",
                        }}
                      >
                        {formatCurrency(netCashflow)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Projected {monthsAhead}-month result
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Account Balances */}
            {forecastData && forecastData.account_balances.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Account Balances
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {forecastData.account_balances.map((acc) => (
                      <Chip
                        key={acc.account}
                        label={`${acc.account.toUpperCase()}: ${formatCurrency(
                          acc.balance,
                        )}`}
                        color={acc.balance >= 0 ? "success" : "error"}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Forecast Table */}
            {forecastData && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#1C4697" }}>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                        Month
                      </TableCell>
                      <TableCell
                        sx={{ color: "white", fontWeight: "bold" }}
                        align="right"
                      >
                        Expected Inflow
                      </TableCell>
                      <TableCell
                        sx={{ color: "white", fontWeight: "bold" }}
                        align="right"
                      >
                        Expected Outflow
                      </TableCell>
                      <TableCell
                        sx={{ color: "white", fontWeight: "bold" }}
                        align="right"
                      >
                        Net Cashflow
                      </TableCell>
                      <TableCell
                        sx={{ color: "white", fontWeight: "bold" }}
                        align="right"
                      >
                        Closing Balance
                      </TableCell>
                      <TableCell
                        sx={{ color: "white", fontWeight: "bold" }}
                        align="center"
                      >
                        Clients
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {forecastData.forecast.map((row, index) => (
                      <TableRow
                        key={row.month}
                        sx={{
                          backgroundColor:
                            row.closing_balance < 0
                              ? "#ffebee"
                              : index % 2 === 0
                              ? "#f5f5f5"
                              : "white",
                        }}
                      >
                        <TableCell>{getMonthName(row.month)}</TableCell>
                        <TableCell align="right" sx={{ color: "#2e7d32" }}>
                          {formatCurrency(row.expected_inflow)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#d32f2f" }}>
                          {formatCurrency(row.expected_outflow)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              row.net_cashflow >= 0 ? "#2e7d32" : "#d32f2f",
                            fontWeight: "bold",
                          }}
                        >
                          {row.net_cashflow >= 0 ? "+" : ""}
                          {formatCurrency(row.net_cashflow)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              row.closing_balance >= 0 ? "#1C4697" : "#d32f2f",
                            fontWeight: "bold",
                          }}
                        >
                          {formatCurrency(row.closing_balance)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.client_count}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : (
          <>
            {/* Variance Controls */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>History Period</InputLabel>
                <Select
                  value={varianceMonths}
                  label="History Period"
                  onChange={(e) => setVarianceMonths(Number(e.target.value))}
                >
                  <MenuItem value={3}>3 Months</MenuItem>
                  <MenuItem value={6}>6 Months</MenuItem>
                  <MenuItem value={12}>12 Months</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Variance Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1C4697" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                      Month
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Forecast Inflow
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Actual Inflow
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Inflow Variance
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Forecast Outflow
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Actual Outflow
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Outflow Variance
                    </TableCell>
                    <TableCell
                      sx={{ color: "white", fontWeight: "bold" }}
                      align="right"
                    >
                      Net Variance
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {varianceData.map((row, index) => (
                    <TableRow
                      key={row.month}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#f5f5f5" : "white",
                      }}
                    >
                      <TableCell>{getMonthName(row.month)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.forecast_inflow)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.actual_inflow)}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          {row.inflow_variance > 0 ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2e7d32" }}
                            />
                          ) : row.inflow_variance < 0 ? (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#d32f2f" }}
                            />
                          ) : null}
                          <Typography
                            sx={{
                              color:
                                row.inflow_variance >= 0
                                  ? "#2e7d32"
                                  : "#d32f2f",
                            }}
                          >
                            {formatCurrency(Math.abs(row.inflow_variance))}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.forecast_outflow)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.actual_outflow)}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          {row.outflow_variance < 0 ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ color: "#2e7d32" }}
                            />
                          ) : row.outflow_variance > 0 ? (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ color: "#d32f2f" }}
                            />
                          ) : null}
                          <Typography
                            sx={{
                              color:
                                row.outflow_variance <= 0
                                  ? "#2e7d32"
                                  : "#d32f2f",
                            }}
                          >
                            {formatCurrency(Math.abs(row.outflow_variance))}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${
                            row.net_variance >= 0 ? "+" : ""
                          }${formatCurrency(row.net_variance)}`}
                          size="small"
                          color={row.net_variance >= 0 ? "success" : "error"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </FinanceContainer>
  );
};

export default CashflowForecast;
